import API from './API';
import { PageParams, PageResponse } from '@models/api';
import { EsimStatus, EsimType } from '@models/stock';
import { OrderEventType, OrderStatus } from '@models/order';
import { ProductSaleType } from './product';

export type GetOrdersParams = {
  orderCode?: string;
  productCode?: string;
  productSaleType?: ProductSaleType;
  productName?: string;
  stockId?: number;
  partnerId?: number;
  orderStatus?: OrderStatus;
  createdAtFrom?: number;
  createdAtTo?: number;
  reservedAtFrom?: number;
  reservedAtTo?: number;
  requestOrderId?: string;
  externalOrderId?: string;
  iccid?: string;
  passportNumber?: string;
};

export type OrderParameterType =
  | 'REDEMPTION_CODE'
  | 'LPA_URL'
  | 'ICCID'
  | 'MSISDN'
  | 'IMEI'
  | 'EID'
  | 'ACTIVATION_DATE';

export type GetOrderEsimsParams = {
  esimType: EsimType;
} & (
  | {
      parameterType: OrderParameterType;
      parameterValue: string;
    }
  | never
);

export type OrderResponse = {
  orderId: number;
  orderCode: String;
  externalReserveCode?: string;
  externalOrderId?: string;
  paymentAmount: number;
  refundAmount: number;
  orderStatus: OrderStatus;
  quantity: number;
  refundQuantity: number;
  product: {
    productId: number;
    productCode: string;
    productName: string;
    productSaleType: ProductSaleType;
  };
  stock: {
    stockId: number;
    stockCode: string;
    esimType: EsimType;
  };
  partner: {
    partnerId: number;
    name: string;
  };
  reservedAt?: number;
  memo?: string;
  createdAt: number;
  updatedAt: number;
};

export type OrderReservationItem = {
  orderReservationItemId: number;
  orderId: number;
  itemStatus: OrderReservationItemStatus;
  email: string;
  airportCode: string;
  customerName: string;
  passportData?: PassportData;
  createdAt: number;
  updatedAt: number;
};
export enum OrderReservationItemStatus {
  RESERVED = 'RESERVED',
  CANCELED = 'CANCELED',
}

export type OrderEsimResponse = {
  esimId: number;
  redemptionCode?: string;
  lpaUrl?: string;
  iccid?: string;
  msisdn?: string;
  pin?: string;
  puk?: string;
  confirmationCode?: string;
  externalEsimId?: string;
  serialNo?: string;
  esimType: EsimType;
  expireAt?: number;
  activationDate?: number;
  validDays?: number;
  imei?: string;
  imsi?: string;
  eid?: string;
  customerName?: string;
  esimStatus: EsimStatus;
  customerDetail?: {
    passportData?: PassportData;
  };
};

export type OrderDelayedEventResponse = {
  delayedEventId: number;
  retried: number;
  eventType: string;
  esimInventoryId: number;
  scheduledAt: number;
  processedAt?: number;
  delayedEventStatus: DelayedEventStatus;
};
export type DelayedEventStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED';

export type RefundType = 'PARTIAL_REFUND' | 'FULL_REFUND';
export type OrderRefundRequest = {
  esimType: EsimType;
  refundType: RefundType;
  esimIds: number[];
  refundFee: number;
};
export type OrderRefundResponse = {
  refundedEsimIds: number[];
};

export type OrderEventResponse = {
  orderEventId: number;
  eventType: OrderEventType;
  owner: string;
  description: string;
  createdAt: number;
};

export type GetUsagesResponse = {
  line?: GetUsagesLineResponse;
  esim?: GetUsagesEsimResponse;
  package?: GetUsagesPackageResponse;
  usage?: GetUsagesUsageResponse;
};

export type GetUsagesLineResponse = {
  iccid?: string;
  msisdn?: string;
  lineStatus: GetUsagesLineStatus;
  imei?: string;
  kycStatus: GetUsagesKycStatusResponse[];
};

export type GetUsagesKycStatusResponse = {
  mcc?: string;
  name?: string;
  docType?: GetUsagesKycDocumentType;
  docExpDate?: string;
  status?: GetUsagesKycStatus;
};

export enum GetUsagesKycDocumentType {
  PASSPORT = 'PASSPORT',
  HK_MACAU_PASS = 'HK_MACAU_PASS',
  HK_ID_CARD = 'HK_ID_CARD',
  MACAU_ID_CARD = 'MACAU_ID_CARD',
  UNKNOWN = 'UNKNOWN',
}

export enum GetUsagesKycStatus {
  TO_BE_CERTIFIED = 'TO_BE_CERTIFIED',
  VERIFYING = 'VERIFYING',
  CERTIFIED = 'CERTIFIED',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  EXPIRED = 'EXPIRED',
  UNKNOWN = 'UNKNOWN',
}

export enum GetUsagesLineStatus {
  ACTIVATED = 'ACTIVATED',
  SUSPENDED = 'SUSPENDED',
  TERMINATED = 'TERMINATED',
  UNKNOWN = 'UNKNOWN',
}

export type GetUsagesEsimResponse = {
  profileStatus: GetUsagesProfileStatus;
  installedAt?: string;
  eid?: string;
  deviceModel?: string;
  lastChangedAt?: string;
};

export enum GetUsagesProfileStatus {
  AVAILABLE = 'AVAILABLE',
  ALLOCATED = 'ALLOCATED',
  RELEASED = 'RELEASED',
  DOWNLOADED = 'DOWNLOADED',
  INSTALLED = 'INSTALLED',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  DELETED = 'DELETED',
  UNKNOWN = 'UNKNOWN',
}

export type GetUsagesPackageResponse = {
  packageStatus: GetUsagesPackageStatus;
  toppedUpAt?: string;
  activationDeadlineAt?: string;
  activatedAt?: string;
  expiresAt?: string;
};

export enum GetUsagesPackageStatus {
  NO_PACKAGE = 'NO_PACKAGE',
  PROVISIONING = 'PROVISIONING',
  UPDATING = 'UPDATING',
  UNUSED = 'UNUSED',
  INUSE = 'INUSE',
  USED = 'USED',
  EXPIRED = 'EXPIRED',
  REFUNDED = 'REFUNDED',
  UNKNOWN = 'UNKNOWN',
}

export type GetUsagesUsageResponse = {
  type: GetUsagesUsageType;
  unit: GetUsagesUsageUnit;
  totalMb?: string;
  usedMb?: string;
  history: GetUsagesUsageHistoryResponse[];
};

export enum GetUsagesUsageType {
  DAILY_RESET = 'DAILY_RESET',
  TOTAL_QUOTA = 'TOTAL_QUOTA',
  UNLIMITED = 'UNLIMITED',
}

export enum GetUsagesUsageUnit {
  MB = 'MB',
  GB = 'GB',
  TB = 'TB',
}

export type GetUsagesUsageHistoryResponse = {
  date?: string;
  usedMb?: string;
};

export const getOrdersCount = async (params: GetOrdersParams) => {
  const { data } = await API.get<PageResponse<number>>('/v1/orders/count', {
    params,
  });
  return data.data[0];
};

export const getOrders = async (
  params: GetOrdersParams & PageParams
): Promise<PageResponse<OrderResponse>> =>
  (await API.get<PageResponse<OrderResponse>>('/v1/orders', { params })).data;

export const getOrder = async (
  orderId: number
): Promise<PageResponse<OrderResponse>> =>
  (await API.get<PageResponse<OrderResponse>>(`/v1/orders/${orderId}`)).data;

export const getOrderEsimUsage = async (
  orderId: number,
  esimInventoryId: number
): Promise<PageResponse<GetUsagesResponse>> =>
  (
    await API.get<PageResponse<GetUsagesResponse>>(
      `/v1/orders/${orderId}/${esimInventoryId}/usage`
    )
  ).data;

export const getOrderReservationItems = async (
  orderId: number
): Promise<PageResponse<OrderReservationItem>> =>
  (
    await API.get<PageResponse<OrderReservationItem>>(
      `/v1/orders/${orderId}/reservation-items`
    )
  ).data;

export const getOrderEsimsCount = async (
  orderId: number,
  params: GetOrderEsimsParams
) => {
  const { data } = await API.get<PageResponse<number>>(
    `/v1/orders/${orderId}/esims/count`,
    { params }
  );

  return data.data[0];
};

export const getOrderEsims = async (
  orderId: number,
  params: GetOrderEsimsParams & PageParams
): Promise<PageResponse<OrderEsimResponse>> =>
  (
    await API.get<PageResponse<OrderEsimResponse>>(
      `/v1/orders/${orderId}/esims`,
      {
        params,
      }
    )
  ).data;

export const getOrderEsimTopUpEvents = async (
  orderId: number
): Promise<PageResponse<OrderDelayedEventResponse>> =>
  (
    await API.get<PageResponse<OrderDelayedEventResponse>>(
      `/v1/orders/${orderId}/events/top-up`
    )
  ).data;

export const processDelayedTopUpOrder = async (
  orderId: number,
  delayedEventId: number
): Promise<PageResponse<string>> =>
  (
    await API.post<PageResponse<string>>(
      `/v1/orders/${orderId}/events/top-up/${delayedEventId}/process`
    )
  ).data;

export const refundOrder = async (
  orderId: number,
  request: OrderRefundRequest
): Promise<PageResponse<OrderRefundRequest>> =>
  (
    await API.post<PageResponse<OrderRefundRequest>>(
      `/v1/orders/${orderId}/refund`,
      request
    )
  ).data;

export const getOrderEvents = async (
  orderId: number
): Promise<PageResponse<OrderEventResponse>> =>
  (
    await API.get<PageResponse<OrderEventResponse>>(
      `/v1/orders/${orderId}/events`
    )
  ).data;

export const addOrderMemo = async (
  orderId: number,
  memo: string
): Promise<void> =>
  API.post(`/v1/orders/${orderId}/memo`, {
    memo: memo,
  });

export const deleteOrderMemo = async (orderId: number, orderEventId: number) =>
  API.delete(`/v1/orders/${orderId}/memo/${orderEventId}`);

export const exportOrderDetailEsimsExcel = async (
  orderId: number,
  params: GetOrderEsimsParams
) => {
  const { data } = await API.get(`/v1/orders/${orderId}/esims/export-excel`, {
    params,
    responseType: 'blob',
  });
  return data;
};

export const exportOrders = async (params: GetOrdersParams) => {
  const { data } = await API.get(`/v1/orders/export-excel`, {
    params,
    responseType: 'blob',
  });
  return data;
};

export const exportOrderEsims = async (params: GetOrdersParams) => {
  const { data } = await API.get(`/v1/orders/esims/export-excel`, {
    params,
    responseType: 'blob',
  });
  return data;
};

export const exportOrderReservationItemsExcel = async (orderId: number) => {
  const { data } = await API.get(
    `/v1/orders/${orderId}/reservation-items/export-excel`,
    {
      responseType: 'blob',
    }
  );
  return data;
};

export type Locale = 'ko' | 'ja' | 'en' | 'zh-CN' | 'zh-TW';
export type CreateOrderRequest = {
  productCode: string;
  quantity: number;
  customerDetails: CustomerDetail[];
  sendEmail: boolean;
};
export type CustomerDetail = {
  activationDate?: string;
  validDays?: number;
  imei?: string;
  eid?: string;
  customerName?: string;
  passportData?: PassportData;
  email?: string;
  phoneNumber?: string;
};
export type PassportData = {
  passportNumber?: string;
  passportName?: string;
  passportBirthdate?: string;
  passportCountry?: string;
  passportGender?: string;
};

export type CreateOrderResponse = {
  orderId: string;
  orderCode: string;
};

export const createOrder = async (request: CreateOrderRequest) =>
  (
    await API.post<PageResponse<CreateOrderResponse>>(
      '/v1/orders/manual',
      request
    )
  ).data;
